import { Grid } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { Address, AddressType, CreateAddress, PartialCreateAddress } from "@/types";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  addressValidationSchema,
  AddressFormInput,
  UpdateAddressInput,
} from "@/components/pages/Settings/validation/addressValidationSchema";
import Modal, { ModalCTAs } from "@/components/molecules/Modal";

import CTA from "@/components/atoms/CTA";
import SelectAddressType from "@/components/molecules/SelectInputs/SelectAddressType";
import SelectCountry from "@/components/molecules/SelectInputs/SelectCountry";
import TextInput from "@/components/atoms/TextInput";
import AddressTypeEnum from "@/constants/enums/AddressTypeEnum";
import { useGetCompanyAddresses } from "@/services/Company";
import { useAuth } from "@/contexts/AuthContext";
import { notifications } from "@mantine/notifications";
import { ErrorNotificationObj } from "@/utils/NotificationUtil";
import { useGetCountries } from "@/services/RegisterCompany";
import SelectRegion from "@/components/molecules/SelectInputs/SelectRegion";

type BaseProps = {
  isAddEditAddressOpen: boolean;
  setIsAddEditAddressOpen: (isOpen: boolean) => void;
  companyId?: number;
};

type CreateModeProps = BaseProps & {
  mode: "create";
  selectedAddress?: CreateAddress | PartialCreateAddress;
  callback: (address: AddressFormInput) => void | Promise<void>;
  countryId?: number;
};

type UpdateModeProps = BaseProps & {
  mode: "update";
  selectedAddress?: Address;
  callback: (address: UpdateAddressInput) => void | Promise<void>;
};

type AddOrEditAddressProps = CreateModeProps | UpdateModeProps;

export const AddOrEditAddressModal = (props: AddOrEditAddressProps) => {
  const { isAddEditAddressOpen, setIsAddEditAddressOpen } = props;
  const { t } = useTranslation();
  const { user } = useAuth();
  const { data: countries } = useGetCountries(user?.language.id as number, {
    enabled: !!user?.language.id,
  });

  const defaultCountryId =
    props.mode === "update"
      ? String(props.selectedAddress?.country?.id ?? 578)
      : String(props.selectedAddress?.countryId ?? props.countryId ?? 578);
  const defaultCountryIso = countries?.find((c) => String(c.id) === defaultCountryId)?.iso2 || "NO";

  const { handleSubmit, control, reset } = useForm<AddressFormInput>({
    resolver: zodResolver(addressValidationSchema(true)),
    mode: "onChange",
    reValidateMode: "onChange",
    values: {
      addressType: props.selectedAddress?.addressType ?? AddressTypeEnum.BILLING,
      address: props.selectedAddress?.address || "",
      address2: props.selectedAddress?.address2 || "",
      address3: props.selectedAddress?.address3 || "",
      place: props.selectedAddress?.place || "",
      postCode: props.selectedAddress?.postCode || "",
      regionId: props.selectedAddress?.regionId,
      countryId: defaultCountryId,
    },
  });

  const onSubmit = async (data: AddressFormInput) => {
    if (props.mode === "update") {
      const { countryId, ...rest } = data;
      const newCountry = countries?.find((country) => country.id === Number(countryId)) || null;
      if (newCountry !== null && props.selectedAddress) {
        props.callback({
          ...rest,
          id: props.selectedAddress.id,
          country: newCountry,
        });
      }
    } else {
      props.callback(data);
    }

    reset();
    setIsAddEditAddressOpen(false);
  };

  const handleClose = () => {
    reset();
    setIsAddEditAddressOpen(false);
  };

  return (
    <Modal
      opened={isAddEditAddressOpen}
      onClose={() => {
        setIsAddEditAddressOpen(false);
        reset();
      }}
      title={t("settings:addAddress")}
      size="lg"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid p={20} data-testid={"add-address-modal"}>
          <Grid.Col span={{ base: 12 }}>
            <Controller
              control={control}
              name="address"
              render={({ field, fieldState }) => (
                <TextInput
                  {...field}
                  label={t("common:address")}
                  data-testid={"add-address-input"}
                  withAsterisk
                  error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                />
              )}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Controller
              control={control}
              name="address2"
              render={({ field, fieldState }) => (
                <TextInput
                  {...field}
                  label={t("common:address2")}
                  error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                />
              )}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Controller
              control={control}
              name="address3"
              render={({ field, fieldState }) => (
                <TextInput
                  {...field}
                  label={t("common:address3")}
                  error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                />
              )}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 5 }}>
            <Controller
              control={control}
              name="addressType"
              render={({ field, fieldState }) => (
                <SelectAddressType
                  {...field}
                  value={field.value}
                  data-testid={"add-address-addresstype"}
                  id="add-address-addresstype"
                  label={t("common:addressType")}
                  withAsterisk
                  error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                />
              )}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 7 }}>
            <Controller
              control={control}
              name="countryId"
              render={({ field, fieldState }) => (
                <SelectCountry
                  id={"country"}
                  valueKey={"id"}
                  labelKey={"name"}
                  data-testid={"add-address-country"}
                  {...field}
                  label={t("common:country")}
                  withAsterisk
                  onChange={(val) => {
                    field.onChange(val);
                  }}
                  error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                />
              )}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 7 }}>
            <Controller
              control={control}
              name="regionId"
              render={({ field, fieldState }) => {
                const countryIso =
                  countries?.find((c) => String(c.id) === String(field.value))?.iso2 || defaultCountryIso;
                return (
                  <SelectRegion
                    id={"region"}
                    valueKey={"id"}
                    labelKey={"name"}
                    countryIso={countryIso ? countryIso : defaultCountryIso}
                    {...field}
                    value={field.value ? String(field.value) : undefined}
                    label={t("common:region")}
                    onChange={(val) => {
                      field.onChange(Number(val));
                    }}
                    error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                  />
                );
              }}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 3 }}>
            <Controller
              control={control}
              name="postCode"
              render={({ field, fieldState }) => (
                <TextInput
                  {...field}
                  data-testid={"add-address-postcode"}
                  label={t("common:postCode")}
                  withAsterisk
                  error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                />
              )}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 9 }}>
            <Controller
              control={control}
              name="place"
              render={({ field, fieldState }) => (
                <TextInput
                  {...field}
                  data-testid={"add-address-place"}
                  label={t("common:place")}
                  withAsterisk
                  error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                />
              )}
            />
          </Grid.Col>
        </Grid>
        <ModalCTAs>
          <CTA id="add-address-cancel" intent="secondary" onClick={handleClose}>
            {t("common:cancel")}
          </CTA>
          <CTA id="add-address-save" intent="primary" type={"submit"} data-testid={"add-address-submit-btn"}>
            {t("common:save")}
          </CTA>
        </ModalCTAs>
      </form>
    </Modal>
  );
};

export default AddOrEditAddressModal;
